import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import { ShieldCheck, ShieldX, Search, QrCode } from "lucide-react";

export default function VerifyCertificate() {
  const { code: urlCode } = useParams();
  const [searchParams] = useSearchParams();
  const urlSig = searchParams.get("sig") || "";
  const [code, setCode] = useState(urlCode || "");
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const check = async (codeToCheck, sig = "") => {
    if (!codeToCheck) return;
    setChecking(true);
    try {
      const qs = sig ? `?sig=${encodeURIComponent(sig)}` : "";
      const { data } = await api.get(`/certificates/verify/${codeToCheck}${qs}`);
      setResult(data);
    } catch {
      setResult({ valid: false, signature_valid: false });
    }
    setChecking(false);
  };

  useEffect(() => {
    if (urlCode) check(urlCode, urlSig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCode, urlSig]);

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrandLogo />
          <Link to="/" className="text-sm text-[#0F4C81] font-semibold hover:underline" data-testid="back-home-link">
            ← Back home
          </Link>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 py-20">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
          Public verification
        </p>
        <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tighter text-[#0A192F] leading-tight">
          Verify a Diplomexa certificate
        </h1>
        <p className="mt-4 text-slate-600">
          Paste any certificate code (e.g. <span className="font-mono">GLOBEX-2026-BD-001</span>) to
          instantly verify its authenticity.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="CERTIFICATE CODE"
            className="h-12 font-mono tracking-widest uppercase"
            data-testid="verify-code-input"
          />
          <Button
            onClick={() => check(code)}
            disabled={checking || !code}
            className="h-12 px-6 bg-[#0F4C81] hover:bg-[#0A3860]"
            data-testid="verify-submit-button"
          >
            <Search className="h-4 w-4 mr-2" />
            {checking ? "Checking…" : "Verify"}
          </Button>
        </div>

        {result && (
          <div className="mt-10" data-testid="verify-result">
            {result.valid ? (
              <div className="rounded-xl border border-emerald-200 bg-white p-8">
                <div className="flex items-center gap-3 text-emerald-600">
                  <ShieldCheck className="h-6 w-6" />
                  <p className="text-sm uppercase tracking-[0.22em] font-bold">Verified</p>
                  {result.signature_valid && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded-full bg-[#0A192F] text-[#D4AF37]"
                      data-testid="verify-qr-badge"
                    >
                      <QrCode className="h-3 w-3" /> Verified via QR
                    </span>
                  )}
                </div>
                <h3 className="mt-4 text-3xl font-serif text-[#0A192F]">
                  {result.certificate.delegate_name}
                </h3>
                <p className="mt-2 text-sm text-slate-500">{result.certificate.event_title}</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                  <Row label="Award" value={result.certificate.award_type} />
                  <Row label="Council" value={result.certificate.council} />
                  <Row label="Country" value={result.certificate.country} />
                  <Row label="Organiser" value={result.certificate.organization_name} />
                  <Row label="Issued" value={new Date(result.certificate.issued_at).toLocaleDateString()} />
                  <Row label="Code" value={result.certificate.code} mono />
                </div>
                {!result.signature_valid && urlCode && (
                  <p className="mt-6 text-xs text-slate-500" data-testid="verify-no-sig-hint">
                    Tip: scan the QR code printed on the certificate for a signed verification.
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-red-200 bg-white p-8">
                <div className="flex items-center gap-3 text-red-600">
                  <ShieldX className="h-6 w-6" />
                  <p className="text-sm uppercase tracking-[0.22em] font-bold">Not Found</p>
                </div>
                <p className="mt-4 text-slate-600">
                  We could not verify this certificate. Double-check the code and try again.
                </p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Mandatory brand attribution on the public certificate verifier */}
      <footer
        data-testid="verify-attribution"
        className="text-center text-xs text-slate-500 py-6"
      >
        Powered by Diplomexa by{" "}
        <a
          href="https://www.destineze.com"
          target="_blank"
          rel="noreferrer"
          className="text-slate-700 hover:text-[#0F4C81] underline-offset-2 hover:underline"
        >
          Destineze
        </a>{" "}
        and{" "}
        <a
          href="https://www.factskoot.com"
          target="_blank"
          rel="noreferrer"
          className="text-slate-700 hover:text-[#0F4C81] underline-offset-2 hover:underline"
        >
          Factskoot
        </a>
      </footer>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between py-2 border-b border-slate-100">
      <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">{label}</span>
      <span className={`text-[#0A192F] font-semibold ${mono ? "font-mono text-xs" : ""}`}>
        {value || "—"}
      </span>
    </div>
  );
}
